import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
  EDonorDocumentErrorCode,
  useCreateDonorDocumentMutation,
} from '@/entities/donor'
import { toaster } from '@/shared/ui'
import { getAxiosErrorData } from '@/shared/utils'
import type { IApiResponse } from '@/shared/types/api'
import { donorDocumentSchema } from './donor-document.schema'
import type { DonorDocumentFormValues } from './donor-document.schema'

interface Options {
  donorId: string
  onSuccess?: () => void
}

/** Error codes that belong to one field — shown under it instead of a toast. */
const ERROR_CODE_TO_FIELD: Partial<
  Record<string, keyof DonorDocumentFormValues>
> = {
  [EDonorDocumentErrorCode.INVALID_FILE_TYPE]: 'file',
  [EDonorDocumentErrorCode.FILE_TOO_LARGE]: 'file',
  [EDonorDocumentErrorCode.DATE_IN_FUTURE]: 'date',
  [EDonorDocumentErrorCode.DOCTOR_TOO_LONG]: 'doctor',
}

const FORM_FIELDS = new Set<string>(['date', 'type', 'note', 'doctor', 'file'])

export function useDonorDocumentForm({ donorId, onSuccess }: Options) {
  const { t } = useTranslation()
  const schema = useMemo(() => donorDocumentSchema(t), [t])

  const { mutateAsync, isPending } = useCreateDonorDocumentMutation(donorId)

  const form = useForm<DonorDocumentFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      date: '',
      type: undefined,
      note: '',
      doctor: '',
      file: undefined,
    },
  })

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      await mutateAsync({
        date: data.date,
        type: data.type,
        note: data.note || undefined,
        doctor: data.doctor || undefined,
        file: data.file,
      })
      toaster.success({ title: t('donor_detail.documents.form.success') })
      form.reset()
      onSuccess?.()
    } catch (error) {
      const body = getAxiosErrorData<IApiResponse<null>>(error)
      const first = body?.errors[0]

      if (first?.code === '200000') {
        first.fields.forEach((field) => {
          if (FORM_FIELDS.has(field.key)) {
            form.setError(field.key as keyof DonorDocumentFormValues, {
              message: field.value,
            })
          }
        })
        return
      }

      const fieldName = first ? ERROR_CODE_TO_FIELD[first.code] : undefined
      if (fieldName && first) {
        form.setError(fieldName, { message: first.message })
        return
      }

      if (first?.message) {
        body?.errors.forEach((item) => toaster.error({ title: item.message }))
        return
      }

      toaster.error({ title: t('donor_detail.documents.form.error') })
    }
  })

  return { form, isPending, onSubmit }
}
